import { useEffect } from 'react'
import { useMediaAsset } from '@/api/queries'
import { StatusBadge } from '@/components/ui/Badge'
import { Field, Input, Textarea } from '@/components/ui/Field'
import { Detail, DetailLink } from '@/components/review/DetailList'
import { ReviewDrawer } from '@/components/review/ReviewDrawer'
import { useEntityDraft } from '@/components/review/useEntityDraft'
import { refLabel } from '@/features/programs/columns'
import { MediaThumb } from './MediaThumb'
import { mediaMeta } from './meta'
import { truncateMiddle } from '@/lib/format'
import type { MediaItem } from '@/types'

interface Draft {
  title: string
  description: string
  tags: string[]
}

const DEFAULTS: Draft = { title: '', description: '', tags: [] }

/**
 * The title and description are editable in place, as a quote's text is. The
 * URL is the asset's identity, and `usable` is the agent's, so both are shown,
 * not offered as fields (docs/03-page-specs.md §7.5).
 */
export function MediaDrawer({
  clientId,
  item,
  tagPool,
  onClose,
  onSave,
  onReview,
  onFavorite,
}: {
  clientId: string
  item: MediaItem | null
  tagPool: string[]
  onClose: () => void
  onSave: (patch: Partial<MediaItem> & { id: string }) => Promise<void>
  onReview: (id: string, status: 'approved' | 'rejected') => void
  onFavorite: (id: string, favorite: boolean) => void
}) {
  const { data: detail } = useMediaAsset(clientId, item?.id ?? null)

  const draft = useEntityDraft<Draft>(
    item ? { title: item.title ?? '', description: item.description ?? '', tags: item.tags } : null,
    DEFAULTS,
  )

  const { reset } = draft
  useEffect(() => {
    if (!item) reset()
  }, [item, reset])

  if (!item) return null

  function close() {
    draft.reset()
    onClose()
  }

  return (
    <ReviewDrawer
      open
      onClose={close}
      title={item.title ?? (item.kind === 'video' ? 'Review video' : 'Review image')}
      subtitle={
        <span className="flex items-center gap-2">
          <StatusBadge status={item.status} />
          <span className="text-ink-faint">{mediaMeta(item)}</span>
        </span>
      }
      creating={false}
      entity={item}
      onSave={() =>
        void onSave({
          id: item.id,
          title: draft.value.title.trim() || null,
          description: draft.value.description.trim() || null,
          tags: draft.value.tags,
        })
      }
      onApprove={() => onReview(item.id, 'approved')}
      onReject={() => onReview(item.id, 'rejected')}
      onFavorite={(next) => onFavorite(item.id, next)}
      tags={draft.value.tags}
      tagPool={tagPool}
      onTagsChange={(tags) => draft.set('tags', tags)}
    >
      <a href={item.url} target="_blank" rel="noreferrer" aria-label="Open original">
        <MediaThumb item={item} />
      </a>

      {!item.usable && (
        <p className="rounded-[4px] bg-warn-50 px-3 py-2 text-[13px] leading-5 text-warn">
          The agent judged this unusable for marketing copy: a logo, icon, chart, or decorative image.
          Approve it anyway if that is wrong.
        </p>
      )}

      <Field label="Title" hint="The caption or stated title on the page. Optional.">
        {(p) => <Input {...p} value={draft.value.title} onChange={(e) => draft.set('title', e.target.value)} />}
      </Field>

      <Field
        label="Description"
        hint="This is what the agent searches on. Describe what is in the frame, not the file."
      >
        {(p) => (
          <Textarea
            {...p}
            rows={3}
            value={draft.value.description}
            onChange={(e) => draft.set('description', e.target.value)}
          />
        )}
      </Field>

      <dl className="space-y-2 border-t border-rule pt-4">
        <Detail label="File">
          <span title={item.url} className="break-all font-mono text-[12px]">
            {truncateMiddle(item.url, 90)}
          </span>
        </Detail>
        <Detail label="Found on">{item.sourceUrl && <DetailLink url={item.sourceUrl} />}</Detail>
        <Detail label="Programs">
          {detail && detail.programs.length > 0 ? detail.programs.map(refLabel).join(', ') : null}
        </Detail>
      </dl>
    </ReviewDrawer>
  )
}
